import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import { getFormattedCommitMessage } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import type { Client } from "../client";
import type { GitRefResource, ProjectResource } from "../resources";
import { HasVariablesInGit } from "../resources";
import type { ModifyProjectVariablesCommand, VariableSetResource } from "../resources/variableSetResource";

export class ProjectVariablesRepository {
    readonly resourceLink = "Variables";
    readonly sensitiveResourceLink = "SensitiveVariables";

    constructor(private readonly client: Client, private readonly project: ProjectResource, private readonly gitRef: GitRefResource | undefined) {
        this.client = client;
    }

    resolveResourceLink() {
        if (HasVariablesInGit(this.project.PersistenceSettings) && this.gitRef !== undefined) {
            return this.gitRef.Links[this.resourceLink];
        }

        return this.project.Links[this.resourceLink];
    }

    resolveSensitiveResourceLink() {
        if (HasVariablesInGit(this.project.PersistenceSettings)) {
            return this.project.Links[this.sensitiveResourceLink];
        }

        throw "This should only be used for projects with Git variables";
    }

    get(): Promise<VariableSetResource> {
        return this.client.get(this.resolveResourceLink());
    }

    getSensitive(): Promise<VariableSetResource> {
        return this.client.get(this.resolveSensitiveResourceLink());
    }

    modify(projectVariables: ModifyProjectVariablesCommand, commitMessage?: CommitMessageWithDetails): Promise<VariableSetResource> {
        const link = projectVariables.Links.Self;

        if (commitMessage) {
            const formattedMessage = getFormattedCommitMessage(commitMessage, "Update variables");
            projectVariables.ChangeDescription = formattedMessage;
        }

        return this.client.update(link, projectVariables).then(() => this.client.get(link));
    }
}

export default ProjectVariablesRepository;
